import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { X, Save, Loader2, ChevronLeft } from 'lucide-react';
import { Note } from '../../types';

interface NoteEditorProps {
  note: Note;
  onClose: () => void;
  onUpdate: (id: string, content: string) => void;
  onDelete: (id: string) => void;
}

export default function NoteEditor({ note, onClose, onUpdate, onDelete }: NoteEditorProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date>(new Date(note.updated_at));
  const [hasChanges, setHasChanges] = useState(false);

  const editor = useEditor({
    extensions: [StarterKit],
    content: note.content,
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg max-w-none focus:outline-none min-h-[200px] p-4',
      },
    },
    onUpdate: ({ editor }) => {
      setHasChanges(true);
    },
  });

  // Auto-save functionality
  useEffect(() => {
    let saveTimeout: NodeJS.Timeout;

    if (hasChanges && editor) {
      setIsSaving(true);
      saveTimeout = setTimeout(async () => {
        await onUpdate(note.id, editor.getHTML());
        setIsSaving(false);
        setLastSaved(new Date());
        setHasChanges(false);
      }, 1000); // Save after 1 second of no changes
    }

    return () => {
      if (saveTimeout) clearTimeout(saveTimeout);
    };
  }, [hasChanges, editor, note.id, onUpdate]);

  const handleManualSave = async () => {
    if (editor && hasChanges) {
      setIsSaving(true);
      await onUpdate(note.id, editor.getHTML());
      setIsSaving(false);
      setLastSaved(new Date());
      setHasChanges(false);
    }
  };

  const getTitle = (content: string) => {
    const div = document.createElement('div');
    div.innerHTML = content;
    const h2 = div.querySelector('h2');
    return h2?.textContent || 'Untitled Note';
  };

  return (
    <div className="h-full flex flex-col bg-yellow-50">
      <div className="p-4 border-b border-yellow-200 bg-yellow-100">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="p-1 hover:bg-yellow-200 rounded flex items-center gap-1 text-gray-600 hover:text-gray-900"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Back to Notes</span>
          </button>
          <div className="h-6 w-px bg-yellow-300" />
          <div className="text-sm text-gray-600">
            {isSaving ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </div>
            ) : hasChanges ? (
              <button
                onClick={handleManualSave}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
              >
                <Save className="w-4 h-4" />
                Save changes
              </button>
            ) : (
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto bg-yellow-50">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}